import { AddException, ErrorMessages } from './../../../shared/constants/global.enum';
import {
  Component,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { TeamManagementService } from '../team-management.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-exception',
  templateUrl: './add-exception.component.html',
  styleUrl: './add-exception.component.scss',
})
export class AddExceptionComponent implements OnInit, OnChanges {
  @Output() close = new EventEmitter<void>();
  @Output() exceptionAdded = new EventEmitter<any>();
  @Input() resourceData: any[] = [];
  @Input() selectedMonth: string = '';
  @Input() selectedYear: string = '';
  @ViewChild('calendar', { static: false }) calendar!: MatCalendar<Date>;

  exceptionForm: FormGroup;
  resourceDropdownOutlined = true;
  resourceDropdownOptions: any[] = [];
  resourceDropdownFormControl = 'resource';
  resourceDropdownPreSelectedValue = '';
  resourceDropdownPreSelectedLabel = 'Select Resource';
  currentDate: Date = new Date();
  calendarStartDate: Date = new Date();
  selectedDates: Date[] = [];
  selectedDateValue: Date = new Date();
  startDate: Date | null = null;
  endDate: Date | null = null;
  AddExceptionLabels = AddException;
  isSubmitting: boolean = false;
  private resourcesLoaded: boolean = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private teamManagementService: TeamManagementService,
    private toastr: ToastrService,
  ) {
    this.exceptionForm = this.fb.group({
      resource: ['', Validators.required],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      exceptionDate: [null],
      comment: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.setupDateRangeSubscription();
    this.setCalendarStartDate();
    if (this.resourceData && this.resourceData.length > 0) {
      this.loadResourceOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resourceData']) {
      if (changes['resourceData'].currentValue && changes['resourceData'].currentValue.length > 0) {
        this.loadResourceOptions();
      }
    }

    if (changes['selectedMonth'] || changes['selectedYear']) {
      this.setCalendarStartDate();
    }
  }

  private setCalendarStartDate(): void {
    if (this.selectedMonth && this.selectedYear) {
      const month = parseInt(this.selectedMonth, 10) - 1;
      const year = parseInt(this.selectedYear, 10);
      this.calendarStartDate = new Date(year, month, 1);
    } else {
      const currentDate = new Date();
      this.calendarStartDate = currentDate;
    }
  }

  private setupDateRangeSubscription(): void {
    this.exceptionForm.get('startDate')?.valueChanges.subscribe(value => {
      this.startDate = value;
    });

    this.exceptionForm.get('endDate')?.valueChanges.subscribe(value => {
      this.endDate = value;
    });
  }

  private loadResourceOptions(): void {
    if (this.resourcesLoaded) {
      return;
    }

    if (this.resourceData && this.resourceData.length > 0) {
      this.resourceDropdownOptions = this.resourceData?.map((employee: any) => ({
        label: employee.name || employee.resource,
        value: employee.empId || employee.id || employee.resource,
        employeeData: employee,
      })) || [];

      this.resourcesLoaded = true;
    } else {
      this.resourceDropdownOptions = [];
    }
  }

  private getSelectedEmployeeData(selectedValue: string): any {

    const selectedOption = this.resourceDropdownOptions?.find(option => option.value === selectedValue);
    return selectedOption?.employeeData;
  }

  private formatDateRange(startDate: Date, endDate: Date): string {
    const formatDate = (date: Date): string => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return `${formatDate(startDate)}-${formatDate(endDate)}`;
  }

  onClose() {
    this.resetComponent();
    this.close.emit();
  }

  private resetComponent(): void {
    this.resourcesLoaded = false;
    this.isSubmitting = false;
    this.exceptionForm.reset();
    this.resourceDropdownOptions = [];
    this.setCalendarStartDate();
  }

  onSubmit() {
    if (this.isSubmitting) {
      return;
    }

    if (this.exceptionForm.valid) {
      this.isSubmitting = true;

      const formValue = this.exceptionForm.value;

      const selectedEmployee = this.getSelectedEmployeeData(formValue.resource.employeeData.empId);

      let employeeId: string;

      if (selectedEmployee) {
        const rawEmployeeId = selectedEmployee.empId;
        employeeId = rawEmployeeId;
      } else if (formValue.resource) {
        employeeId = String(formValue.resource);
      } else {
        this.toastr.error(ErrorMessages.InvalidResource);
        this.isSubmitting = false;
        return;
      }

      const startDate: Date = formValue.startDate;
      const endDate: Date = formValue.endDate;

      if (!startDate || !endDate) {
        this.toastr.error(ErrorMessages.InvalidDateRange);
        this.isSubmitting = false;
        return;
      }

      const formattedDateRange = this.formatDateRange(startDate, endDate);

      const payload = {
        employeeId: employeeId.toString(),
        exceptionDuration: formattedDateRange,
        comments: formValue.comment.trim()
      };

      if (!employeeId || String(employeeId).trim() === '') {
        this.toastr.error(ErrorMessages.InvalidEmployeeId);
        this.isSubmitting = false;
        return;
      }

      if (!formValue.comment || formValue.comment.trim() === '') {
        this.toastr.error(ErrorMessages.CommentsRequired);
        this.isSubmitting = false;
        return;
      }

      if (!formattedDateRange || formattedDateRange.trim() === '') {
        this.toastr.error(ErrorMessages.InvalidDateFormat);
        this.isSubmitting = false;
        return;
      }

      this.teamManagementService.addException(payload).subscribe({
        next: (response) => {
          if (response && (response.success !== false)) {
          } else {
          }

          this.toastr.success(`${ErrorMessages.ExceptionAddedSuccess} ${payload.employeeId}`);

          this.isSubmitting = false;

          this.exceptionAdded.emit({
            employeeId: payload.employeeId,
            exceptionDuration: payload.exceptionDuration,
            comments: payload.comments,
            startDate: startDate,
            endDate: endDate,
            response: response
          });
          this.close.emit();
        },
        error: (error) => {
          this.toastr.error(error.error?.message || ErrorMessages.SubmissionFailed);
          this.isSubmitting = false;
        }
      });
    } else {
      this.toastr.error(ErrorMessages.FillRequiredFields);
      this.markFormGroupTouched();
      this.isSubmitting = false;
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.exceptionForm.controls).forEach(key => {
      const control = this.exceptionForm.get(key);
      control?.markAsTouched();
    });
  }
}
